import React, { useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, Loader2, FileText, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const Section = styled.section`
  padding: 80px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SectionHeadline = styled.div`
  text-align: center;
  margin-bottom: 40px;
  max-width: 800px;
`;

const MainTitle = styled.h2`
  font-size: 48px;
  color: #0f172a;
  font-weight: 800;
  margin-bottom: 16px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const MainDescription = styled.p`
  font-size: 20px;
  color: #334155;
  line-height: 1.6;
  font-weight: 500;
`;

const Container = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 24px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.8);
  overflow: hidden;
  display: grid;
  grid-template-columns: 320px 1fr;
  min-height: 550px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const Sidebar = styled.div`
  background: #002147;
  padding: 40px;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%);
  }
`;

const ContentArea = styled.div`
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  @media (max-width: 600px) {
    padding: 32px 24px;
  }
`;

const StepIndicator = styled.div`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #64748b;
  font-weight: 700;
  margin-bottom: 12px;
`;

const QuestionTitle = styled(motion.h3)`
  font-size: 32px;
  color: #0f172a;
  margin-bottom: 32px;
  font-weight: 700;
  line-height: 1.2;
`;

const OptionsGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const OptionButton = styled(motion.button) <{ selected?: boolean }>`
  padding: 20px 24px;
  border-radius: 16px;
  border: 2px solid ${props => props.selected ? '#002147' : '#e2e8f0'};
  background: ${props => props.selected ? '#f0f9ff' : 'white'};
  color: ${props => props.selected ? '#002147' : '#334155'};
  font-size: 18px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.selected ? '0 4px 12px rgba(0, 33, 71, 0.1)' : 'none'};

  &:hover {
    border-color: #002147;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 20px;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  font-size: 18px;
  margin-bottom: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #002147;
    box-shadow: 0 0 0 4px rgba(0, 33, 71, 0.1);
  }
`;

const Button = styled(motion.button)`
  padding: 18px 36px;
  background: #002147;
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: center;
  margin-top: 12px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: #003366;
    transform: translateY(-2px);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,0.2);
  border-radius: 3px;
  margin-top: 24px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: #4ecdc4;
`;

const ReportContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 30px 60px -15px rgba(0, 0, 0, 0.2);
  width: 100%;
  overflow: hidden;
  margin: 0 auto;
  border: 1px solid #e2e8f0;
`;

const ReportHeader = styled.div`
  background: linear-gradient(135deg, #002147 0%, #003366 100%);
  padding: 60px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -50px;
    right: -50px;
    width: 250px;
    height: 250px;
    background: radial-gradient(circle, rgba(78,205,196,0.15) 0%, transparent 70%);
  }
`;

const ReportMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: space-between;
  border-top: 1px solid rgba(255,255,255,0.2);
  padding-top: 24px;
  margin-top: 40px;
  font-size: 14px;
  color: rgba(255,255,255,0.8);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ReportBody = styled.div`
  padding: 60px;
  background: white;
  color: #334155;
  font-size: 18px;
  line-height: 1.8;
  
  .report-markdown h3 {
    color: #002147;
    font-size: 28px;
    font-weight: 800;
    margin-top: 48px;
    margin-bottom: 24px;
    border-bottom: 2px solid #f1f5f9;
    padding-bottom: 12px;
  }
  
  .report-markdown h4 {
    color: #0f172a;
    font-size: 22px;
    font-weight: 700;
    margin-top: 32px;
    margin-bottom: 16px;
  }

  .report-markdown p {
    margin-bottom: 24px;
  }

  .report-markdown ul {
    padding-left: 24px;
    margin-bottom: 24px;
  }

  .report-markdown li {
    margin-bottom: 12px;
  }

  .report-markdown strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

const ReportFooter = styled.div`
  background: #f8fafc;
  padding: 40px 60px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 16px;
`;

type QuestionType = 'yesno' | 'single' | 'form';

interface Question {
    id: string;
    type: QuestionType;
    title: string;
    options?: string[];
}

const questions: Question[] = [
    {
        id: 'inquiry_handling',
        type: 'yesno',
        title: "Are you able to deal with 100% of inquiries every single day?",
    },
    {
        id: 'multilingual',
        type: 'yesno',
        title: "Do you have staff trained to handle student queries in foreign languages?",
    },
    {
        id: 'manual_work',
        type: 'yesno',
        title: "If an announcement is to be made for a delay due to holidays, do you send emails manually?",
    },
    {
        id: 'lms_usage',
        type: 'single',
        title: "What is the LMS that you use?",
        options: [
            "Moodle",
            "Canvas",
            "Blackboard",
            "Custom / Other",
            "None"
        ]
    },
    {
        id: 'preference',
        type: 'single',
        title: "Would you rather prefer an AI assistant for enrolled students or an enrollment advisor for prospects?",
        options: [
            "AI Assistant for Enrolled Students",
            "Enrollment Advisor for Prospects",
            "Both"
        ]
    },
    {
        id: 'support_cost',
        type: 'single',
        title: "How much do you spend for your support staff on average monthly?",
        options: [
            "Less than $1,000",
            "$1,000 - $5,000",
            "$5,000 - $10,000",
            "More than $10,000"
        ]
    },
    {
        id: 'contact',
        type: 'form',
        title: "Get Your Automation Report",
    }
];

export const EducationReadinessQuiz: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, any>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [aiReport, setAiReport] = useState<string>('');
    const [loadingMessage, setLoadingMessage] = useState<string>('');

    const currentQuestion = questions[currentStep];
    const progress = ((currentStep + 1) / questions.length) * 100;

    const handleOptionClick = (option: string) => {
        setAnswers({ ...answers, [currentQuestion.id]: option });
        setTimeout(() => nextStep(), 250);
    };

    const handleFormChange = (field: string, value: string) => {
        setAnswers({
            ...answers,
            [currentQuestion.id]: { ...(answers[currentQuestion.id] || {}), [field]: value }
        });
    };

    const nextStep = () => {
        if (currentStep < questions.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setLoadingMessage('Scraping your website...');
        
        let messageInterval = setInterval(() => {
            setLoadingMessage(prev => {
                if (prev === 'Scraping your website...') return 'Analyzing support stack...';
                if (prev === 'Analyzing support stack...') return 'Generating personalized report...';
                return 'Finalizing details...';
            });
        }, 3500);

        try {
            const res = await fetch('/api/education-report', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ answers }),
            });
            const data = await res.json();
            if (data.aiReport) {
                setAiReport(data.aiReport);
            } else {
                setAiReport("### Analysis Complete\n\nWe couldn't generate a highly personalized report at this time. However, based on your inputs, implementing an AI Educator with SmushLabs can significantly reduce your support costs and automate your LMS operations.");
            }
        } catch (error) {
            console.error('Failed to submit report', error);
            setAiReport("### Analysis Complete\n\nAn error occurred while generating the report. Please contact support.");
        } finally {
            clearInterval(messageInterval);
            setIsSubmitting(false);
            setIsSubmitted(true);
        }
    };

    if (isSubmitted) {
        const contactAnswers = answers['contact'] || {};
        const websiteUrl = contactAnswers.website ? contactAnswers.website.replace(/^https?:\/\//, '') : 'Your Company';
        const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

        return (
            <Section>
                <Container style={{ maxWidth: '900px' }}>
                    <ReportContainer
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <ReportHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px', color: '#4ecdc4', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px' }}>
                                <FileText size={20} /> Executive Summary
                            </div>
                            <h2 style={{ fontSize: '42px', fontWeight: 800, margin: '0 0 16px 0', lineHeight: 1.1, color: '#ffffff' }}>
                                AI Automation Forecast
                            </h2>
                            <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.9)', margin: 0, maxWidth: '600px', lineHeight: 1.5 }}>
                                A customized analysis of how SmushLabs can eliminate manual overhead and accelerate enrollments for {websiteUrl}.
                            </p>
                            <ReportMeta>
                                <div>Prepared For: <strong>{contactAnswers.name || 'Founder'}</strong></div>
                                <div>Date: <strong>{date}</strong></div>
                                <div>Report ID: <strong>SML-{Math.floor(Math.random() * 10000)}</strong></div>
                            </ReportMeta>
                        </ReportHeader>

                        <ReportBody>
                            <div className="report-markdown max-w-none">
                                <ReactMarkdown>{aiReport}</ReactMarkdown>
                            </div>
                        </ReportBody>
                        
                        <ReportFooter>
                           <Button onClick={() => {
                               const el = window.document.getElementById('demo-request');
                               if (el) {
                                   el.scrollIntoView({behavior: 'smooth'});
                               } else {
                                   window.location.href = '/contact';
                               }
                           }} style={{ background: 'white', color: '#002147', border: '2px solid #002147', padding: '16px 32px', margin: 0, width: 'auto', fontSize: '16px' }}>
                               Request a Demo
                           </Button>
                           <Button onClick={() => window.location.href = '/get-started'} style={{ background: '#002147', color: 'white', border: 'none', padding: '16px 32px', margin: 0, width: 'auto', fontSize: '16px' }}>
                               Create an AI Educator <ChevronRight size={18} />
                           </Button>
                        </ReportFooter>
                    </ReportContainer>
                </Container>
            </Section>
        );
    }

    return (
        <Section id="audit">
            <SectionHeadline>
                <MainTitle>Is Your Institute Automation-Ready?</MainTitle>
                <MainDescription>
                    Take this 2-minute audit to uncover hidden costs and see how much you could save.
                </MainDescription>
            </SectionHeadline>

            <Container>
                <Card>
                    <Sidebar>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px', color: '#4ecdc4', fontWeight: 600 }}>
                                <Sparkles size={20} /> Free Audit
                            </div>
                            <h2 style={{ fontSize: '36px', fontWeight: 800, lineHeight: 1.1, marginBottom: '24px', color: '#ffffff' }}>
                                Let's calculate your potential savings.
                            </h2>
                            <p style={{ fontSize: '18px', lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.9)' }}>
                                See exactly where manual work is costing you money and how to fix it.
                            </p>
                        </div>
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '10px', color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600 }}>
                                <span>PROGRESS</span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <ProgressBar>
                                <ProgressFill
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.5 }}
                                />
                            </ProgressBar>
                        </div>
                    </Sidebar>

                    <ContentArea>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <StepIndicator>QUESTION {currentStep + 1} OF {questions.length}</StepIndicator>
                                <QuestionTitle>{currentQuestion.title}</QuestionTitle>

                                {currentQuestion.type === 'yesno' && (
                                    <OptionsGrid>
                                        {['Yes', 'No'].map((option) => (
                                            <OptionButton
                                                key={option}
                                                selected={answers[currentQuestion.id] === option}
                                                onClick={() => handleOptionClick(option)}
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                            >
                                                {option}
                                                {answers[currentQuestion.id] === option && <Check size={24} color="#002147" />}
                                            </OptionButton>
                                        ))}
                                    </OptionsGrid>
                                )}

                                {currentQuestion.type === 'single' && (
                                    <OptionsGrid>
                                        {currentQuestion.options?.map((option) => (
                                            <OptionButton
                                                key={option}
                                                selected={answers[currentQuestion.id] === option}
                                                onClick={() => handleOptionClick(option)}
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                            >
                                                {option}
                                                {answers[currentQuestion.id] === option && <Check size={24} color="#002147" />}
                                            </OptionButton>
                                        ))}
                                    </OptionsGrid>
                                )}

                                {currentQuestion.type === 'form' && (
                                    <div>
                                        <Input
                                            placeholder="Full Name"
                                            value={answers[currentQuestion.id]?.name || ''}
                                            onChange={(e) => handleFormChange('name', e.target.value)}
                                        />
                                        <Input
                                            placeholder="Institute Website"
                                            value={answers[currentQuestion.id]?.website || ''}
                                            onChange={(e) => handleFormChange('website', e.target.value)}
                                        />
                                        <Input
                                            placeholder="Work Email"
                                            type="email"
                                            value={answers[currentQuestion.id]?.email || ''}
                                            onChange={(e) => handleFormChange('email', e.target.value)}
                                        />
                                        <Button
                                            onClick={handleSubmit}
                                            disabled={isSubmitting || !answers[currentQuestion.id]?.email}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={20} />
                                                    <span style={{fontSize: '16px'}}>{loadingMessage}</span>
                                                </>
                                            ) : (
                                                <>
                                                    Reveal My Report <ChevronRight size={20} />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </ContentArea>
                </Card>
            </Container>
        </Section>
    );
};

export default EducationReadinessQuiz;
